import {
  StripeRecurringInterval,
  PaymentMethod as CommonPaymentMethod,
  isAchPadAllowed,
  Undefinable,
  OrganizationCountry,
  FormType,
  PaymentMethod,
  countryHasPad,
  countryHasAch,
  countryHasSepa,
  countryHasCashApp,
  SEPA_MAX_PAYMENT_AMOUNT,
  DAF_MINIMUM_AMOUNT,
  getCardMaximumAmount,
} from "@simplyk/common";

import { isTicketing } from "./form";

import {
  DonationFormFrontendPageOutput,
  TicketingFrontendPageOutput,
  TicketingOccurrenceOutput,
} from "@/common/types/trpc";

interface ListPaymentMethodsArgs {
  orgCardMaximumAmount: number;
  formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput;
  selectedOccurrence?: TicketingOccurrenceOutput | null;
  areBankPaymentMethodsAllowed: boolean;
  organizationCountry: OrganizationCountry;
  totalAmount: number;
  totalAmountWithTip?: number;
  formType?: FormType;
  stripeRecurrenceInterval?: StripeRecurringInterval | null;
  isAuction?: boolean;
  isStripeCustomAccountActive: boolean;
  isPreviewTemplateMode?: boolean;
  isEmbed: boolean;
  isFreeTicketing: boolean;
  isApplePayGooglePayEnabledOnEmbed: boolean;
  isCashAppEnabled?: boolean;
}

export const getIsFreeTicketing = ({
  formData,
  displayedFormAmount,
}: {
  formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput;
  displayedFormAmount: number;
}): boolean => {
  if (!isTicketing(formData)) {
    return false;
  }
  const hasOnlyFreeRates = Boolean(formData.rates?.every((rate) => rate.amount === 0 && !rate.isPayWhatYouCan));
  const isInherentlyFree = hasOnlyFreeRates && !formData.extraDonation;
  return isInherentlyFree && displayedFormAmount === 0;
};

export const listPaymentMethods = (args: ListPaymentMethodsArgs): CommonPaymentMethod[] => {
  if (!args.formData || args.isFreeTicketing) {
    return [];
  }
  const cardMaximumAmount = getCardMaximumAmount(args.orgCardMaximumAmount);
  const isYearlyDonation =
    args.formType === FormType.DonationForm && args.stripeRecurrenceInterval === StripeRecurringInterval.year;

  const { isPadAllowed, isAchAllowed, isSepaAllowed } = isAchPadAllowed({
    startUtc: args.selectedOccurrence?.startUtc ? new Date(args.selectedOccurrence.startUtc) : null,
    endUtc: args.selectedOccurrence?.endUtc ? new Date(args.selectedOccurrence.endUtc) : null,
  });

  const isPadDisabled = !isPadAllowed || Boolean(args.isAuction);
  const isAchDisabled = !isAchAllowed || Boolean(args.isAuction);

  const chequeMinimumAmount = (args.formData as DonationFormFrontendPageOutput).chequeMinimumAmount ?? null;

  const paymentMethodsAllowed = {
    [CommonPaymentMethod.Daf]:
      args.formType === FormType.DonationForm &&
      Boolean(
        (args.formData as DonationFormFrontendPageOutput).dafEnabled && args.formData.organization?.isDafEligible
      ) &&
      !args.stripeRecurrenceInterval &&
      args.totalAmount >= DAF_MINIMUM_AMOUNT,
    [CommonPaymentMethod.Card]: args.totalAmount <= cardMaximumAmount,
    [CommonPaymentMethod.Pad]: isBankDebitAllowed({
      currentAmount: args.totalAmount,
      cardMaximumAmount,
      isYearlyDonation,
      isCountryAllowed: countryHasPad(args.organizationCountry),
      isDisabled: !args.areBankPaymentMethodsAllowed || isPadDisabled,
      isStripeCustomAccountActive: Boolean(args.isStripeCustomAccountActive || args.isPreviewTemplateMode),
    }),
    [CommonPaymentMethod.Ach]: isBankDebitAllowed({
      currentAmount: args.totalAmount,
      cardMaximumAmount,
      isYearlyDonation,
      isCountryAllowed: countryHasAch(args.organizationCountry),
      isDisabled: !args.areBankPaymentMethodsAllowed || isAchDisabled,
      isStripeCustomAccountActive: Boolean(args.isStripeCustomAccountActive || args.isPreviewTemplateMode),
    }),
    [CommonPaymentMethod.Sepa]:
      (args.totalAmountWithTip ?? args.totalAmount) <= SEPA_MAX_PAYMENT_AMOUNT &&
      isBankDebitAllowed({
        currentAmount: args.totalAmount,
        cardMaximumAmount,
        isYearlyDonation,
        isCountryAllowed: countryHasSepa(args.organizationCountry),
        isDisabled: !args.areBankPaymentMethodsAllowed || !isSepaAllowed || Boolean(args.isAuction),
        isStripeCustomAccountActive: args.isStripeCustomAccountActive,
      }),
    [CommonPaymentMethod.ApplePayOrGooglePay]:
      (!args.isEmbed || args.isApplePayGooglePayEnabledOnEmbed) &&
      !isYearlyDonation &&
      args.totalAmount <= cardMaximumAmount,
    [CommonPaymentMethod.Cheque]:
      (chequeMinimumAmount !== null &&
        !args.stripeRecurrenceInterval &&
        args.totalAmount >= chequeMinimumAmount &&
        !args.isAuction) ||
      args.isPreviewTemplateMode,
    [CommonPaymentMethod.Cash]: chequeMinimumAmount !== null && args.isAuction,
    [CommonPaymentMethod.CashApp]:
      Boolean(args.isCashAppEnabled) &&
      countryHasCashApp(args.organizationCountry) &&
      !args.isEmbed &&
      !args.stripeRecurrenceInterval &&
      !args.isAuction &&
      args.totalAmount <= cardMaximumAmount,
  };

  const filteredPaymentMethods = Object.entries(paymentMethodsAllowed)
    .filter(([, isAllowed]) => isAllowed)
    .map(([method]) => method as CommonPaymentMethod);

  return filteredPaymentMethods.length === 0 ? [CommonPaymentMethod.Card] : filteredPaymentMethods;
};

const isBankDebitAllowed = ({
  currentAmount,
  cardMaximumAmount,
  isCountryAllowed,
  isYearlyDonation,
  isDisabled,
  isStripeCustomAccountActive,
}: {
  currentAmount: number;
  cardMaximumAmount: number;
  isYearlyDonation: boolean;
  isCountryAllowed: boolean;
  isDisabled: boolean;
  isStripeCustomAccountActive: boolean;
}): boolean => {
  if (!isStripeCustomAccountActive) {
    return false;
  }
  if (!isCountryAllowed || isDisabled) {
    return false;
  }

  // Always display bank debit method for yearly donations as it is the only payment method allowed.
  if (isYearlyDonation) {
    return true;
  }

  // In the case cards have been disabled, we show the bank debit option even if the minimum amount is not reached.
  if (cardMaximumAmount < currentAmount) {
    return true;
  }

  return currentAmount >= 0;
};

export const isPaymentMethodAlwaysComplete = (paymentMethod: Undefinable<CommonPaymentMethod>): boolean => {
  return paymentMethod !== CommonPaymentMethod.Ach && paymentMethod !== CommonPaymentMethod.Sepa;
};

export const hasRequiredSepaMandateConsent = (
  paymentMethod: CommonPaymentMethod | undefined,
  sepaMandateAccepted: boolean | undefined
): boolean => paymentMethod !== CommonPaymentMethod.Sepa || sepaMandateAccepted === true;

export const doesPaymentMethodAllowTip = (paymentMethod: CommonPaymentMethod | undefined): boolean => {
  return (
    !paymentMethod ||
    ![CommonPaymentMethod.Cheque, CommonPaymentMethod.Cash, CommonPaymentMethod.Daf].includes(paymentMethod)
  );
};

const commonToGqlPaymentMethodMap: Record<CommonPaymentMethod, PaymentMethod> = {
  [CommonPaymentMethod.Card]: PaymentMethod.Card,
  [CommonPaymentMethod.Pad]: PaymentMethod.Pad,
  [CommonPaymentMethod.Ach]: PaymentMethod.Ach,
  [CommonPaymentMethod.Sepa]: PaymentMethod.Sepa,
  [CommonPaymentMethod.ApplePayOrGooglePay]: PaymentMethod.ApplePayOrGooglePay,
  [CommonPaymentMethod.Cash]: PaymentMethod.Cash,
  [CommonPaymentMethod.Cheque]: PaymentMethod.Cheque,
  [CommonPaymentMethod.Daf]: PaymentMethod.Daf,
  [CommonPaymentMethod.CashApp]: PaymentMethod.CashApp,
  [CommonPaymentMethod.Free]: PaymentMethod.Free,
  [CommonPaymentMethod.InKind]: PaymentMethod.InKind,
  [CommonPaymentMethod.Stocks]: PaymentMethod.Stocks,
  [CommonPaymentMethod.Other]: PaymentMethod.Other,
  [CommonPaymentMethod.TapToPay]: PaymentMethod.TapToPay,
  [CommonPaymentMethod.Unknown]: PaymentMethod.Other,
  [CommonPaymentMethod.Transfer]: PaymentMethod.Other,
  [CommonPaymentMethod.Manual]: PaymentMethod.Other,
  [CommonPaymentMethod.PayPal]: PaymentMethod.PayPal,
  [CommonPaymentMethod.Venmo]: PaymentMethod.Venmo,
  [CommonPaymentMethod.Pledge]: PaymentMethod.Pledge,
  [CommonPaymentMethod.InPersonCash]: PaymentMethod.InPersonCash,
  [CommonPaymentMethod.Invoice]: PaymentMethod.Invoice,
};

export const convertCommonPaymentMethodToGqlPaymentMethod = (
  commonPaymentMethod: CommonPaymentMethod
): PaymentMethod => {
  const gqlPaymentMethod = commonToGqlPaymentMethodMap[commonPaymentMethod];
  return gqlPaymentMethod ?? PaymentMethod.Other;
};

const gqlToCommonPaymentMethodMap: Record<PaymentMethod, CommonPaymentMethod> = {
  [PaymentMethod.Card]: CommonPaymentMethod.Card,
  [PaymentMethod.Pad]: CommonPaymentMethod.Pad,
  [PaymentMethod.Ach]: CommonPaymentMethod.Ach,
  [PaymentMethod.Sepa]: CommonPaymentMethod.Sepa,
  [PaymentMethod.ApplePayOrGooglePay]: CommonPaymentMethod.ApplePayOrGooglePay,
  [PaymentMethod.Cash]: CommonPaymentMethod.Cash,
  [PaymentMethod.Cheque]: CommonPaymentMethod.Cheque,
  [PaymentMethod.Daf]: CommonPaymentMethod.Daf,
  [PaymentMethod.CashApp]: CommonPaymentMethod.CashApp,
  [PaymentMethod.Free]: CommonPaymentMethod.Free,
  [PaymentMethod.InKind]: CommonPaymentMethod.InKind,
  [PaymentMethod.Stocks]: CommonPaymentMethod.Stocks,
  [PaymentMethod.Other]: CommonPaymentMethod.Other,
  [PaymentMethod.TapToPay]: CommonPaymentMethod.TapToPay,
  [PaymentMethod.Manual]: CommonPaymentMethod.Manual,
  [PaymentMethod.Transfer]: CommonPaymentMethod.Transfer,
  [PaymentMethod.Unknown]: CommonPaymentMethod.Unknown,
  [PaymentMethod.PayPal]: CommonPaymentMethod.PayPal,
  [PaymentMethod.Venmo]: CommonPaymentMethod.Venmo,
  [PaymentMethod.Pledge]: CommonPaymentMethod.Pledge,
  [PaymentMethod.Invoice]: CommonPaymentMethod.Invoice,
  [PaymentMethod.InPersonCash]: CommonPaymentMethod.InPersonCash,
};

export const convertGqlPaymentMethodToCommonPaymentMethod = (gqlPaymentMethod: PaymentMethod): CommonPaymentMethod => {
  const commonPaymentMethod = gqlToCommonPaymentMethodMap[gqlPaymentMethod];
  return commonPaymentMethod ?? CommonPaymentMethod.Other;
};
